import { el, btn, backButton } from '@/ui/dom';
import type { AdminStats } from '@/account/AccountService';
import { confirmAction } from '@/ui/confirm';
import {
  ageLabel,
  contextLabel,
  reasonLabel,
  type AdminReport,
  type ModerationAction,
} from '@/social/moderation';

/**
 * AdminScreen.ts — the M101b ADMIN DASHBOARD (owner ask 2026-07-28). Visible
 * only to accounts with the server-side `profiles.is_admin` flag (granted by
 * SQL — the stats RPC re-checks it, so the client bit is display-only).
 *
 * Three jobs, deliberately small:
 *   PLAYER REPORTS (M156, App Store 1.2) — every report players send, newest
 *     first, with the three answers: RENAME (fresh RACER-name, the old name
 *     retired), SUSPEND (rename + locked out of online play and the Racer
 *     Club), DISMISS. Our promise to players and App Review: act within 24
 *     hours. Each report is also emailed to info@aplvglobal.com.
 *   QA FLOWS — one-tap launchers for test experiences (the onboarding
 *     preview runs with NO server writes; more flows are one row each).
 *   GROWTH — a light stats module: player counts, onboarding conversion,
 *     referral signups, SP volume, weekly actives, purchases.
 */

export interface AdminCallbacks {
  fetchStats(): Promise<AdminStats | null>;
  /** M156: the reports list (open only, or everything incl. closed). */
  fetchReports(status: 'open' | 'all'): Promise<AdminReport[] | null>;
  /** M156: one moderator action; resolves the server's answer. */
  moderate(
    reportId: string,
    action: ModerationAction,
  ): Promise<{ status: string; newName?: string; closed?: number }>;
  reducedMotion(): boolean;
  onRunOnboarding(): void;
  onHowToPlay(): void;
  onReplayIntro(): void;
  onBack(): void;
}

export class AdminScreen {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private statsGrid: HTMLDivElement | null = null;
  private statsNote: HTMLDivElement | null = null;
  /** Staleness token — a re-show mid-fetch must not paint old numbers. */
  private fetchSeq = 0;
  private reportsList: HTMLDivElement | null = null;
  private reportsNote: HTMLDivElement | null = null;
  private reportsToggle: HTMLButtonElement | null = null;
  private showClosed = false;
  private reportSeq = 0;

  constructor(
    parent: HTMLElement,
    private cb: AdminCallbacks,
  ) {
    this.root = el('div', 'screen screen-admin screen-cine', parent);
    this.root.style.display = 'none';
    this.panel = el('div', 'admin-panel', this.root);
    this.build();
  }

  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  show(): void {
    this.root.style.display = '';
    void this.loadReports();
    void this.loadStats();
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }

  private build(): void {
    const head = el('div', 'admin-head', this.panel);
    const kicker = el('div', 'lb-kicker', head);
    el('span', 'mhead-rule', kicker);
    el('span', 'lb-kicker-text', kicker).textContent = 'OWNER TOOLS';
    el('span', 'mhead-rule', kicker);
    const title = el('div', 'lb-title', head);
    title.textContent = 'ADMIN DASHBOARD';
    title.dataset.text = 'ADMIN DASHBOARD';
    el('div', 'lb-sub', head).textContent = 'REPORTS · QA FLOWS · GROWTH AT A GLANCE';

    // ---- M156: player reports (act within 24 hours) ------------------------
    const repHead = el('div', 'admin-section admin-section-row', this.panel);
    el('span', '', repHead).textContent = 'PLAYER REPORTS';
    this.reportsToggle = btn('SHOW CLOSED', 'btn btn-ghost admin-mini', repHead, () => {
      this.showClosed = !this.showClosed;
      if (this.reportsToggle) this.reportsToggle.textContent = this.showClosed ? 'OPEN ONLY' : 'SHOW CLOSED';
      void this.loadReports();
    });
    this.reportsNote = el('div', 'admin-note', this.panel);
    this.reportsList = el('div', 'admin-reports', this.panel);

    // ---- QA flows ----------------------------------------------------------
    el('div', 'admin-section', this.panel).textContent = 'TEST FLOWS';
    const flows = el('div', 'admin-flows', this.panel);
    const flow = (label: string, sub: string, fn: () => void): void => {
      const b = btn('', 'admin-flow', flows, fn);
      el('span', 'admin-flow-label', b).textContent = label;
      el('span', 'admin-flow-sub', b).textContent = sub;
    };
    flow('RUN ONBOARDING FLOW', 'The new-player welcome · preview, no server writes', () =>
      this.cb.onRunOnboarding(),
    );
    flow('HOW TO PLAY GUIDE', 'The four-chapter guide + intro cinematic', () =>
      this.cb.onHowToPlay(),
    );
    flow('REPLAY TITLE INTRO', 'Reload — the globe bounces into the cup again', () =>
      this.cb.onReplayIntro(),
    );

    // ---- Growth stats ------------------------------------------------------
    el('div', 'admin-section', this.panel).textContent = 'GROWTH';
    this.statsNote = el('div', 'admin-note', this.panel);
    this.statsGrid = el('div', 'admin-stats', this.panel);

    // M114b (owner): the footer BACK button is gone — the ONE shared chevron
    // pins to the screen's upper-left, like every other menu.
    backButton(this.root, () => this.cb.onBack());
  }

  private async loadReports(note?: string): Promise<void> {
    if (!this.reportsList || !this.reportsNote) return;
    const seq = ++this.reportSeq;
    this.reportsNote.textContent = note ?? 'LOADING…';
    const rows = await this.cb.fetchReports(this.showClosed ? 'all' : 'open');
    if (seq !== this.reportSeq || !this.reportsList || !this.reportsNote) return;
    this.reportsList.replaceChildren();
    if (!rows) {
      this.reportsNote.textContent = 'REPORTS UNAVAILABLE — CHECK THE CONNECTION';
      return;
    }
    this.reportsNote.textContent = note ?? '';
    if (rows.length === 0) {
      el('div', 'admin-report-empty', this.reportsList).textContent = this.showClosed
        ? 'No reports yet.'
        : 'No open reports. New ones land here and in the info@aplvglobal.com inbox — act within 24 hours.';
      return;
    }
    for (const r of rows) this.reportCard(r);
  }

  private reportCard(r: AdminReport): void {
    const card = el('div', `admin-report admin-report-${r.status}`, this.reportsList!);
    const top = el('div', 'admin-report-top', card);
    el('span', 'admin-report-name', top).textContent = r.name.toUpperCase();
    el('span', 'admin-report-kind', top).textContent = r.kind === 'club' ? 'CLUB NAME' : 'PLAYER';
    if (r.status === 'open' && r.openOnTarget > 1) {
      el('span', 'admin-report-count', top).textContent = `${r.openOnTarget} REPORTS`;
    }
    el('div', 'admin-report-why', card).textContent =
      `${reasonLabel(r.reason)} · ${contextLabel(r.context)} · ${ageLabel(r.createdAt)}`;
    el('div', 'admin-report-meta', card).textContent =
      `Reported by ${r.reporter ?? 'a deleted account'}${r.room ? ` · room ${r.room}` : ''}`;
    if (r.details) el('div', 'admin-report-note', card).textContent = `“${r.details}”`;
    el('div', 'admin-report-match', card).textContent =
      r.kind === 'club'
        ? r.clubNow
          ? `Club now named ${r.clubNow.toUpperCase()}`
          : 'No club carries this name now.'
        : r.userNow
          ? `Racer ID now named ${r.userNow.toUpperCase()}${r.userSuspended ? ' · SUSPENDED' : ''}`
          : "No Racer ID carries this name now — a computer racer's name, or the player already changed it.";
    if (r.status !== 'open') {
      el('div', 'admin-report-done', card).textContent =
        r.status === 'dismissed' ? 'DISMISSED' : `DONE · ${(r.action ?? '').replace('_', ' ').toUpperCase()}`;
    }

    const actions = el('div', 'admin-report-actions', card);
    const act = (action: ModerationAction, done: (res: { newName?: string; closed?: number }) => string): void => {
      for (const b of actions.querySelectorAll('button')) b.disabled = true;
      void this.cb.moderate(r.id, action).then((res) => {
        const msg =
          res.status === 'ok'
            ? done(res)
            : res.status === 'no_account'
              ? 'No Racer ID carries that name now — nothing to rename.'
              : res.status === 'no_club'
                ? 'That club is gone — nothing to rename.'
                : 'That did not go through — check the connection and try again.';
        void this.loadReports(msg.toUpperCase());
      });
    };
    const closed = (n?: number): string => (n && n > 1 ? ` · ${n} reports closed` : '');
    if (r.status === 'open') {
      if (r.kind === 'player' && r.userId && r.userNow) {
        const who = r.userNow.toUpperCase();
        btn('RENAME', 'btn btn-secondary admin-mini', actions, () =>
          confirmAction({
            title: `RENAME ${who}?`,
            body: 'They get a fresh RACER-name and this name is retired for good. Every open report on it closes.',
            confirmLabel: 'RENAME',
            reducedMotion: this.cb.reducedMotion(),
            onConfirm: () => act('rename', (res) => `Renamed to ${res.newName ?? 'a new name'}${closed(res.closed)}`),
          }),
        );
        btn('SUSPEND', 'btn btn-danger-ghost admin-mini', actions, () =>
          confirmAction({
            title: `SUSPEND ${who}?`,
            body:
              'Renames them, retires this name, removes them from their club, and locks their Racer ID out of online races and the Racer Club. Offline play still works. You can undo it from this list.',
            confirmLabel: 'SUSPEND',
            reducedMotion: this.cb.reducedMotion(),
            onConfirm: () => act('suspend', (res) => `Suspended (now ${res.newName ?? 'renamed'})${closed(res.closed)}`),
          }),
        );
      } else if (r.kind === 'club' && r.clubId && r.clubNow) {
        const club = r.clubNow.toUpperCase();
        btn('RENAME CLUB', 'btn btn-secondary admin-mini', actions, () =>
          confirmAction({
            title: `RENAME ${club}?`,
            body: 'The club gets a neutral CLUB-name and this name is retired for good. Members stay.',
            confirmLabel: 'RENAME CLUB',
            reducedMotion: this.cb.reducedMotion(),
            onConfirm: () => act('rename_club', (res) => `Club renamed to ${res.newName ?? 'a new name'}${closed(res.closed)}`),
          }),
        );
      }
      btn('DISMISS', 'btn btn-ghost admin-mini', actions, () =>
        act('dismiss', (res) => `Dismissed${closed(res.closed)}`),
      );
    } else if (r.kind === 'player' && r.userId && r.userSuspended) {
      btn('UNSUSPEND', 'btn btn-ghost admin-mini', actions, () =>
        act('unsuspend', () => 'Suspension lifted'),
      );
    }
  }

  private async loadStats(): Promise<void> {
    if (!this.statsGrid || !this.statsNote) return;
    const seq = ++this.fetchSeq;
    this.statsNote.textContent = 'LOADING…';
    this.statsGrid.replaceChildren();
    const stats = await this.cb.fetchStats();
    if (seq !== this.fetchSeq || !this.statsGrid || !this.statsNote) return;
    if (!stats) {
      this.statsNote.textContent = 'STATS UNAVAILABLE — CHECK THE CONNECTION';
      return;
    }
    this.statsNote.textContent = '';
    const tile = (value: string, label: string): void => {
      const t = el('div', 'admin-stat', this.statsGrid!);
      el('span', 'admin-stat-num', t).textContent = value;
      el('span', 'admin-stat-label', t).textContent = label;
    };
    const fmt = (n: number): string => n.toLocaleString('en-US');
    tile(fmt(stats.totalPlayers), 'RACER IDS');
    tile(fmt(stats.new24h), 'NEW · 24H');
    tile(fmt(stats.new7d), 'NEW · 7 DAYS');
    tile(fmt(stats.onboardedPlayers), 'ONBOARDED');
    tile(fmt(stats.referralSignups), 'REFERRAL SIGNUPS');
    tile(fmt(stats.active7d), 'ACTIVE · 7 DAYS');
    tile(fmt(stats.spTotal), 'XP AWARDED · ALL TIME');
    tile(fmt(stats.sp7d), 'XP AWARDED · 7 DAYS');
    tile(fmt(stats.purchasesTotal), 'SHOP PURCHASES');
  }
}
